import { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import Pagination from '@/components/base/Pagination';
import FilterSidebar, { type FilterState } from './components/FilterSidebar';
import usePageMeta from '@/hooks/usePageMeta';

import { media } from '@/utils/data';

const PAGE_SIZE = 8;

const SORTS = [
  { key: 'latest', label: '最新' },
  { key: 'hot', label: '最热门' },
  { key: 'rating', label: '评分' },
];

const EMPTY_FILTER: FilterState = { regions: [], years: [], genres: [] };

export default function CategoryPage() {
  const { type } = useParams();
  const mediaType: 'movie' | 'tv' = type === 'tv' ? 'tv' : 'movie';

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTER);
  const [sort, setSort] = useState('hot');
  const [page, setPage] = useState(1);
  const [extraPages, setExtraPages] = useState(0);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    setFilters(EMPTY_FILTER);
    setPage(1);
    setExtraPages(0);
    setSort('hot');
    setMobileFilterOpen(false);
    setSearchParams({}, { replace: true });
  }, [mediaType, setSearchParams]);

  const pool = useMemo(() => media.filter((item) => item.type === mediaType), [mediaType]);

  const yearOptions = useMemo(
    () => Array.from(new Set(pool.map((item) => item.year))).sort((a, b) => b - a),
    [pool],
  );
  const regionOptions = useMemo(
    () => Array.from(new Set(pool.map((item) => item.region))),
    [pool],
  );
  const genreOptions = useMemo(
    () => Array.from(new Set(pool.flatMap((item) => item.genres))),
    [pool],
  );

  const filtered = useMemo(() => {
    const list = pool.filter((item) => {
      const regionOk = filters.regions.length === 0 || filters.regions.includes(item.region);
      const yearOk = filters.years.length === 0 || filters.years.includes(item.year);
      const genreOk =
        filters.genres.length === 0 || item.genres.some((g) => filters.genres.includes(g));
      return regionOk && yearOk && genreOk;
    });
    if (sort === 'latest') list.sort((a, b) => b.year - a.year || b.hot - a.hot);
    else if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    else list.sort((a, b) => b.hot - a.hot);
    return list;
  }, [pool, filters, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visibleCount = (page + extraPages) * PAGE_SIZE;
  const visible = filtered.slice((page - 1) * PAGE_SIZE, visibleCount);
  const canLoadMore = visibleCount < filtered.length;

  const handlePageChange = (next: number) => {
    setPage(next);
    setExtraPages(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTER);
    setPage(1);
    setExtraPages(0);
  };

  const isMovie = mediaType === 'movie';
  const title = isMovie ? '电影' : '电视剧';
  const description = isMovie
    ? '聚焦院线佳作与经典影片，聚合科幻、悬疑、爱情、历史等多元类型，帮你快速筛选出值得一看的电影。'
    : '汇聚热播剧集与口碑佳作，涵盖古装、都市、科幻、医疗等题材，追剧不掉队。';

  usePageMeta({
    title: `${title} - 御宅屋影视推荐与筛选`,
    description: `${title}频道${description}`,
    keywords: `${title},${title}推荐,${title}排行榜,御宅屋影视,影视筛选`,
  });

  return (
    <div className="w-full px-4 md:px-8 pb-16">
      <div className="pt-2">
        <Breadcrumb items={[{ label: '影视分类' }, { label: title }]} />
      </div>

      <div className="rounded-lg bg-gradient-to-r from-primary-900/60 to-background-100 border border-background-200/70 p-6 md:p-8 mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          {title}频道
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          {description}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24">
            <FilterSidebar
              filters={filters}
              yearOptions={yearOptions}
              regionOptions={regionOptions}
              genreOptions={genreOptions}
              onChange={(next) => {
                setFilters(next);
                setPage(1);
                setExtraPages(0);
              }}
              onReset={handleReset}
            />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-500">
              共找到 <span className="text-accent-400 font-semibold">{filtered.length}</span> 部{title}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileFilterOpen((v) => !v)}
                className="lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:border-accent-500/60 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-200/60 border border-background-300/50">
                {SORTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => {
                      setSort(s.key);
                      setPage(1);
                      setExtraPages(0);
                    }}
                    className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      sort === s.key
                        ? 'bg-accent-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {mobileFilterOpen && (
            <div className="lg:hidden mb-6">
              <FilterSidebar
                filters={filters}
                yearOptions={yearOptions}
                regionOptions={regionOptions}
                genreOptions={genreOptions}
                onChange={(next) => {
                  setFilters(next);
                  setPage(1);
                  setExtraPages(0);
                }}
                onReset={handleReset}
              />
            </div>
          )}

          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                {visible.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>

              {canLoadMore && (
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    onClick={() => setExtraPages((v) => v + 1)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:border-accent-500/60 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    加载更多
                  </button>
                </div>
              )}

              {extraPages === 0 && (
                <Pagination page={page} totalPages={totalPages} onChange={handlePageChange} />
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200/70">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
                暂无符合条件的影视作品
              </h3>
              <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或直接查看全部作品</p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}